import { create } from 'zustand'
import { api } from '@/lib/api'
import type { BidStatus } from '@/lib/schemas'

// ===================
// Pipeline Types
// ===================

export interface PipelineStageRow {
  status: BidStatus
  count: number
  totalValue: number
}

export interface QuarterlyDelta {
  currentQuarter: number
  previousQuarter: number
  pctChange: number
}

export interface PipelineSummary {
  stages: PipelineStageRow[]
  delta: QuarterlyDelta
}

// ===================
// Recent Activity Types
// ===================

/** Org-wide activity item riding on the pipeline-summary response. */
export interface OrgActivityItem {
  id: string
  bidId: string
  bidTitle: string
  userId: string
  userName: string | null
  action: string
  targetType: string
  targetId: string | null
  targetName: string | null
  metadata: Record<string, unknown>
  createdAt: string
}

interface PipelineSummaryResponse extends PipelineSummary {
  /** Optional so a frontend deploy landing before the backend degrades gracefully. */
  recentActivity?: OrgActivityItem[]
}

// ===================
// Insight Types
// ===================

export type InsightSeverity = 'critical' | 'attention' | 'positive'
export type InsightPhrasingSource = 'canonical' | 'llm'

export interface DashboardInsight {
  id: string
  ruleId: string
  severity: InsightSeverity
  message: string
  phrasingSource: InsightPhrasingSource
  actionLabel: string
  actionUrl: string
  relatedBidId: string | null
  relatedArtifactId: string | null
  relatedSectionId: string | null
  computedAt: string
}

export interface InsightsResponse {
  insights: DashboardInsight[]
  totalCount: number
  showingTop: number
}

// ===================
// Store
// ===================

interface DashboardState {
  pipeline: PipelineSummary | null
  pipelineLoading: boolean
  pipelineError: string | null
  recentActivity: OrgActivityItem[]

  insights: DashboardInsight[]
  insightsTotalCount: number
  insightsLoading: boolean
  insightsError: string | null

  fetchPipelineSummary: () => Promise<void>
  fetchInsights: () => Promise<void>
  dismissInsight: (id: string, ruleId: string) => Promise<void>
}

export const useDashboardStore = create<DashboardState>()((set, get) => ({
  pipeline: null,
  pipelineLoading: false,
  pipelineError: null,
  recentActivity: [],

  insights: [],
  insightsTotalCount: 0,
  insightsLoading: false,
  insightsError: null,

  fetchPipelineSummary: async () => {
    set({ pipelineLoading: true, pipelineError: null })
    try {
      const data = await api.get<PipelineSummaryResponse>('/dashboard/pipeline-summary')
      const { recentActivity, ...pipeline } = data
      set({ pipeline, recentActivity: recentActivity ?? [], pipelineLoading: false })
    } catch {
      set({ pipelineLoading: false, pipelineError: 'Failed to load pipeline summary' })
    }
  },

  fetchInsights: async () => {
    set({ insightsLoading: true, insightsError: null })
    try {
      const data = await api.get<InsightsResponse>('/dashboard/insights')
      set({
        insights: data.insights,
        insightsTotalCount: data.totalCount,
        insightsLoading: false,
      })
    } catch {
      set({ insightsLoading: false, insightsError: 'Failed to load insights' })
    }
  },

  dismissInsight: async (id, ruleId) => {
    // Optimistic update — remove from the list immediately.
    const prev = get().insights
    set({
      insights: prev.filter((i) => i.id !== id),
      insightsTotalCount: Math.max(get().insightsTotalCount - 1, 0),
    })
    try {
      await api.post(`/dashboard/insights/${id}/dismiss`, { ruleId })
    } catch {
      // Revert on failure
      set({ insights: prev, insightsTotalCount: get().insightsTotalCount + 1 })
    }
  },
}))
